// src/pages/AdminAuditLogPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiInfo,
  FiAlertTriangle,
  FiX,
  FiClipboard
} from 'react-icons/fi';
import styles from './AdminAuditLogPage.module.css';
import { AuditService } from '../services/audit.service';
import type { AuditEvent, AuditEventsResponse } from '../types/Audit';
import { useNotification } from '../context/NotificationContext';

function toISODate(d: Date) {
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function safeT(t: (k: string) => string, key: string, fallback: string) {
  const v = t(key);
  return v === key ? fallback : v;
}

function formatDateTime(iso: string, locale = 'es-MX') {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function toTitleCase(value: string) {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

type Order = 'desc' | 'asc';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const RESULT_OPTIONS = [
  { value: 'all', key: 'audit.filter_all', fallback: 'Todos' },
  { value: 'SUCCESS', key: 'audit.result_success', fallback: 'SUCCESS' },
  { value: 'FAIL', key: 'audit.result_fail', fallback: 'FAIL' }
];

const ORIGIN_OPTIONS = [
  { value: 'all', key: 'audit.filter_all', fallback: 'Todos' },
  { value: 'WEB', key: 'audit.origin_web', fallback: 'WEB' },
  { value: 'APP', key: 'audit.origin_app', fallback: 'APP' },
  { value: 'SERVICE', key: 'audit.origin_service', fallback: 'SERVICE' },
  { value: 'BATCH', key: 'audit.origin_batch', fallback: 'BATCH' }
];

const ORDER_OPTIONS: { value: Order; labelKey: string; fallback: string }[] = [
  { value: 'desc', labelKey: 'audit.order_desc', fallback: 'Más recientes' },
  { value: 'asc', labelKey: 'audit.order_asc', fallback: 'Más antiguos' }
];

const EVENT_GROUP_OPTIONS = [
  { value: '', key: 'audit.filter_all', fallback: 'Todos' },
  { value: 'SESSION_', key: 'audit.group_sessions', fallback: 'Sesiones' },
  { value: 'BANK_ACCOUNT_', key: 'audit.group_bank_accounts', fallback: 'CLABE' },
  { value: 'MFA_BANK_ACCOUNT_', key: 'audit.group_mfa_bank', fallback: 'MFA CLABE' },
  { value: 'WITHDRAWAL_', key: 'audit.group_withdrawals', fallback: 'Retiros' },
  { value: 'MFA_WITHDRAWAL_', key: 'audit.group_mfa_withdrawals', fallback: 'MFA Retiros' },
  { value: 'INVESTMENT_', key: 'audit.group_investments', fallback: 'Inversiones' },
  { value: 'PAYMENT_', key: 'audit.group_payments', fallback: 'Pagos' },
  { value: 'MFA_INVESTMENT_', key: 'audit.group_mfa_investments', fallback: 'MFA Inversiones' }
];

const EVENT_LABELS: Record<string, { key: string; fallback: string }> = {
  MFA_LOGIN_SUCCESS: { key: 'audit.event.MFA_LOGIN_SUCCESS', fallback: 'MFA de inicio de sesión exitoso' },
  MFA_LOGIN_FAILED: { key: 'audit.event.MFA_LOGIN_FAILED', fallback: 'MFA de inicio de sesión fallido' },
  MFA_CODE_SENT: { key: 'audit.event.MFA_CODE_SENT', fallback: 'Código MFA enviado' },
  MFA_CODE_VERIFIED: { key: 'audit.event.MFA_CODE_VERIFIED', fallback: 'Código MFA verificado' },
  MFA_CODE_FAILED: { key: 'audit.event.MFA_CODE_FAILED', fallback: 'Código MFA inválido' },
  LOGIN_PARTIAL: { key: 'audit.event.LOGIN_PARTIAL', fallback: 'Login parcial (MFA pendiente)' },
  LOGIN_FAILED: { key: 'audit.event.LOGIN_FAILED', fallback: 'Login fallido' },
  LOGIN_BLOCKED: { key: 'audit.event.LOGIN_BLOCKED', fallback: 'Login bloqueado' },
  LOGOUT: { key: 'audit.event.LOGOUT', fallback: 'Cierre de sesión' },
  SESSION_CREATED: { key: 'audit.event.SESSION_CREATED', fallback: 'Sesión creada' },
  SESSION_EXPIRED: { key: 'audit.event.SESSION_EXPIRED', fallback: 'Sesión expirada' },
  SESSION_INVALIDATED: { key: 'audit.event.SESSION_INVALIDATED', fallback: 'Sesión invalidada' },
  REGISTER: { key: 'audit.event.REGISTER', fallback: 'Registro de usuario' },
  REGISTER_ADMIN: { key: 'audit.event.REGISTER_ADMIN', fallback: 'Registro de administrador' },
  MFA_SETUP_CONFIRMED: { key: 'audit.event.MFA_SETUP_CONFIRMED', fallback: 'Configuración MFA completada' },
  MFA_ENABLED: { key: 'audit.event.MFA_ENABLED', fallback: 'MFA habilitado' },
  OTP_SENT: { key: 'audit.event.OTP_SENT', fallback: 'Código OTP enviado' },
  OTP_VERIFIED: { key: 'audit.event.OTP_VERIFIED', fallback: 'Código OTP verificado' },
  PASSWORD_RESET_REQUESTED: { key: 'audit.event.PASSWORD_RESET_REQUESTED', fallback: 'Solicitud de restablecer contraseña' },
  PASSWORD_RESET_COMPLETED: { key: 'audit.event.PASSWORD_RESET_COMPLETED', fallback: 'Restablecimiento de contraseña' },
  PASSWORD_CHANGED: { key: 'audit.event.PASSWORD_CHANGED', fallback: 'Contraseña cambiada' },
  ACCOUNT_UNLOCKED: { key: 'audit.event.ACCOUNT_UNLOCKED', fallback: 'Cuenta desbloqueada' },
  USER_APPROVED: { key: 'audit.event.USER_APPROVED', fallback: 'Usuario aprobado' },
  USER_REJECTED: { key: 'audit.event.USER_REJECTED', fallback: 'Usuario rechazado' },
  USER_MISSING_DOCS: { key: 'audit.event.USER_MISSING_DOCS', fallback: 'Usuario con documentos faltantes' },
  PROFILE_COMPLETED: { key: 'audit.event.PROFILE_COMPLETED', fallback: 'Perfil completado' },
  WITHDRAWAL_REQUESTED: { key: 'audit.event.WITHDRAWAL_REQUESTED', fallback: 'Solicitud de retiro creada' },
  WITHDRAWAL_APPROVED: { key: 'audit.event.WITHDRAWAL_APPROVED', fallback: 'Solicitud de retiro aprobada' },
  WITHDRAWAL_REJECTED: { key: 'audit.event.WITHDRAWAL_REJECTED', fallback: 'Solicitud de retiro rechazada' },
  WITHDRAWAL_EXECUTED: { key: 'audit.event.WITHDRAWAL_EXECUTED', fallback: 'Retiro ejecutado' },
  BANK_ACCOUNT_CREATED: { key: 'audit.event.BANK_ACCOUNT_CREATED', fallback: 'CLABE registrada' },
  BANK_ACCOUNT_UPDATED: { key: 'audit.event.BANK_ACCOUNT_UPDATED', fallback: 'CLABE actualizada' },
  BANK_ACCOUNT_DELETED: { key: 'audit.event.BANK_ACCOUNT_DELETED', fallback: 'CLABE eliminada' },
  BANK_ACCOUNT_VERIFIED: { key: 'audit.event.BANK_ACCOUNT_VERIFIED', fallback: 'CLABE verificada' },
  INVESTMENT_CLOSE_CALCULATED: { key: 'audit.event.INVESTMENT_CLOSE_CALCULATED', fallback: 'Cálculo de cierre de inversión' },
  PAYMENT_COMPLETED: { key: 'audit.event.PAYMENT_COMPLETED', fallback: 'Pago completado' },
  PAYMENT_FAILED: { key: 'audit.event.PAYMENT_FAILED', fallback: 'Pago fallido' },
  PAYMENT_CANCELLED: { key: 'audit.event.PAYMENT_CANCELLED', fallback: 'Pago cancelado' },
  PAYMENT_MFA_FAILED: { key: 'audit.event.PAYMENT_MFA_FAILED', fallback: 'MFA de pago fallido' },
  PAYMENT_MFA_SUCCESS: { key: 'audit.event.PAYMENT_MFA_SUCCESS', fallback: 'MFA de pago exitoso' },
  PAYPAL_ORDER_CREATED: { key: 'audit.event.PAYPAL_ORDER_CREATED', fallback: 'Orden PayPal creada' },
  PAYPAL_CAPTURE_ATTEMPT: { key: 'audit.event.PAYPAL_CAPTURE_ATTEMPT', fallback: 'Intento de captura PayPal' },
  AUDIT_LOG_VIEW: { key: 'audit.event.AUDIT_LOG_VIEW', fallback: 'Consulta de bitácora' },
};

function formatEventLabel(
  eventType: string,
  t: (k: string) => string,
) {
  const entry = EVENT_LABELS[eventType];
  if (entry) return safeT(t, entry.key, entry.fallback);
  if (eventType.startsWith('MFA_')) {
    const tail = eventType.slice(4);
    const actionMap: Record<string, { key: string; fallback: string }> = {
      CODE_SENT: { key: 'audit.event.MFA_CODE_SENT', fallback: 'Código MFA enviado' },
      CODE_VERIFIED: { key: 'audit.event.MFA_CODE_VERIFIED', fallback: 'Código MFA verificado' },
      CODE_FAILED: { key: 'audit.event.MFA_CODE_FAILED', fallback: 'Código MFA inválido' },
    };
    const suffix = Object.keys(actionMap).find(k => tail.endsWith(k));
    const actionLabel = suffix ? safeT(t, actionMap[suffix].key, actionMap[suffix].fallback) : 'MFA';
    const purposeRaw = suffix ? tail.slice(0, -suffix.length).replace(/_+$/, '') : tail;
    const purposeLabel = toTitleCase(purposeRaw.replace(/_/g, ' '));
    return `${purposeLabel} · ${actionLabel}`;
  }
  return toTitleCase(eventType.replace(/_/g, ' '));
}

const Chip: React.FC<{ value: string; tone?: 'ok' | 'bad' | 'neutral' }> = ({ value, tone = 'neutral' }) => {
  return <span className={`${styles.chip} ${styles[`chip_${tone}`]}`}>{value}</span>;
};

const Modal: React.FC<{ open: boolean; title: string; onClose: () => void; children: React.ReactNode }> = ({
  open,
  title,
  onClose,
  children
}) => {
  if (!open) return null;

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FiInfo /> {title}
          </div>
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
            <FiX />
          </button>
        </div>
        <div className={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
};

export default function AdminAuditLogPage() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();

  const locale = i18n.language.startsWith('es') ? 'es-MX' : 'en-US';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [res, setRes] = useState<AuditEventsResponse | null>(null);

  // filtros
  const [q, setQ] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventGroup, setEventGroup] = useState('');
  const [actorUserId, setActorUserId] = useState('');
  const [subjectUserId, setSubjectUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [origin, setOrigin] = useState<'all' | string>('all');
  const [result, setResult] = useState<'all' | string>('all');
  const [order, setOrder] = useState<Order>('desc');
  const [datePreset, setDatePreset] = useState<'today' | 'yesterday' | 'last7' | 'last30' | 'custom'>('last7');

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toISODate(d);
  });
  const [to, setTo] = useState(() => toISODate(new Date()));

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // detalle
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const rows = useMemo(() => res?.data || [], [res]);
  const meta = res?.meta;
  const activeFilters = useMemo(() => {
    const items: Array<{ id: string; label: string }> = [];
    if (q.trim()) items.push({ id: 'q', label: `${safeT(t, 'audit.filter_search', 'Buscar')}: ${q.trim()}` });
    if (eventType.trim())
      items.push({
        id: 'eventType',
        label: `${safeT(t, 'audit.filter_event_type', 'Tipo de evento')}: ${eventType.trim()}`
      });
    if (eventGroup)
      items.push({
        id: 'eventGroup',
        label: `${safeT(t, 'audit.filter_event_group', 'Grupo de evento')}: ${
          safeT(t, EVENT_GROUP_OPTIONS.find(o => o.value === eventGroup)?.key || 'audit.filter_all', 'Todos')
        }`
      });
    if (actorUserId.trim())
      items.push({
        id: 'actor',
        label: `${safeT(t, 'audit.filter_actor', 'Actor (ID)')}: ${actorUserId.trim()}`
      });
    if (userName.trim())
      items.push({
        id: 'userName',
        label: `${safeT(t, 'audit.filter_user_name', 'Nombre de usuario')}: ${userName.trim()}`
      });
    if (subjectUserId.trim())
      items.push({
        id: 'subject',
        label: `${safeT(t, 'audit.filter_subject', 'Sujeto (ID)')}: ${subjectUserId.trim()}`
      });
    if (origin !== 'all')
      items.push({
        id: 'origin',
        label: `${safeT(t, 'audit.filter_origin', 'Origen')}: ${origin}`
      });
    if (result !== 'all')
      items.push({
        id: 'result',
        label: `${safeT(t, 'audit.filter_result', 'Resultado')}: ${result}`
      });
    if (order)
      items.push({
        id: 'order',
        label: `${safeT(t, 'audit.filter_order', 'Orden')}: ${safeT(
          t,
          ORDER_OPTIONS.find(o => o.value === order)?.labelKey || 'audit.order_desc',
          order === 'asc' ? 'Más antiguos' : 'Más recientes'
        )}`
      });
    if (pageSize)
      items.push({
        id: 'pageSize',
        label: `${safeT(t, 'audit.filter_page_size', 'Tamaño')}: ${pageSize}`
      });
    if (from || to)
      items.push({
        id: 'range',
        label: `${safeT(t, 'audit.filter_date_range', 'Rango')}: ${from || '—'} → ${to || '—'}`
      });
    return items;
  }, [q, eventType, eventGroup, actorUserId, userName, subjectUserId, origin, result, order, pageSize, from, to, t]);

  const removeFilter = (id: string) => {
    if (id === 'q') setQ('');
    if (id === 'eventType') setEventType('');
    if (id === 'eventGroup') setEventGroup('');
    if (id === 'actor') setActorUserId('');
    if (id === 'userName') setUserName('');
    if (id === 'subject') setSubjectUserId('');
    if (id === 'origin') setOrigin('all');
    if (id === 'result') setResult('all');
    if (id === 'order') setOrder('desc');
    if (id === 'pageSize') setPageSize(20);
    if (id === 'range') setDatePreset('last7');
  };

  const load = async (opts?: { resetPage?: boolean }) => {
    setLoading(true);
    setError(null);

    const nextPage = opts?.resetPage ? 1 : page;
    const effectiveEventType = eventType.trim() || eventGroup || undefined;

    try {
      const data = await AuditService.getEvents({
        q: q.trim() || undefined,
        event_type: effectiveEventType,
        actor_user_id: actorUserId.trim() || undefined,
        subject_user_id: subjectUserId.trim() || undefined,
        user_name: userName.trim() || undefined,
        origin: origin === 'all' ? undefined : origin,
        result: result === 'all' ? undefined : result,
        from: from || undefined,
        to: to || undefined,
        order,
        page: nextPage,
        pageSize
      });

      setRes(data || null);
      if (opts?.resetPage) setPage(1);
    } catch (e: any) {
      setRes(null);
      const msg =
        (e?.payload?.message && String(e.payload.message)) ||
        e?.message ||
        safeT(t, 'audit.load_error', 'No se pudo cargar la bitácora.');
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      load({ resetPage: true });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    q,
    eventType,
    eventGroup,
    actorUserId,
    subjectUserId,
    userName,
    origin,
    result,
    order,
    from,
    to,
    pageSize,
    datePreset
  ]);

  useEffect(() => {
    if (datePreset === 'custom') return;
    const now = new Date();
    if (datePreset === 'today') {
      const today = toISODate(now);
      setFrom(today);
      setTo(today);
      return;
    }
    if (datePreset === 'yesterday') {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const day = toISODate(d);
      setFrom(day);
      setTo(day);
      return;
    }
    if (datePreset === 'last30') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      setFrom(toISODate(d));
      setTo(toISODate(now));
      return;
    }
    const d = new Date();
    d.setDate(d.getDate() - 7);
    setFrom(toISODate(d));
    setTo(toISODate(now));
  }, [datePreset]);

  const onApplyFilters = () => load({ resetPage: true });

  const canPrev = (meta?.page || 1) > 1;
  const canNext = (meta?.totalPages || 1) > (meta?.page || 1);

  const goPrev = () => {
    if (!canPrev) return;
    const next = page - 1;
    setPage(next);
    setTimeout(() => load(), 0);
  };

  const goNext = () => {
    if (!canNext) return;
    const next = page + 1;
    setPage(next);
    setTimeout(() => load(), 0);
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.titleRow}>
            <div className={styles.titleIcon}>
              <FiClipboard />
            </div>
            <div>
              <h1 className={styles.title}>{safeT(t, 'audit.title', 'Bitácora de auditoría')}</h1>
              <p className={styles.subtitle}>
                {safeT(t, 'audit.subtitle', 'Registro append-only de acciones críticas para trazabilidad.')}
              </p>
            </div>
          </div>
        </div>

        <button type="button" className={styles.refreshBtn} onClick={() => load()} disabled={loading}>
          <FiRefreshCw />
          {safeT(t, 'common.refresh', 'Actualizar')}
        </button>
      </header>

      <section className={styles.card}>
        <div className={styles.cardTop}>
          <div className={styles.filters}>
            <div className={`${styles.filterItem} ${styles.filterItemWide}`}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_search', 'Buscar')}</span>
              <div className={styles.searchField} role="search">
                <FiSearch className={styles.searchIcon} />
                <input
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  className={styles.searchInput}
                  placeholder={safeT(t, 'audit.search_placeholder', 'Buscar por tipo, user id, IP, error...')}
                  onKeyDown={e => {
                    if (e.key === 'Enter') onApplyFilters();
                    if (e.key === 'Escape') setQ('');
                  }}
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_event_type', 'Tipo de evento')}</span>
              <input
                value={eventType}
                onChange={e => {
                  setEventType(e.target.value);
                  if (e.target.value) setEventGroup('');
                }}
                className={styles.input}
                placeholder={safeT(t, 'audit.event_type_placeholder', 'event_type (opcional)')}
                disabled={loading}
              />
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_event_group', 'Grupo de evento')}</span>
              <div className={styles.selectWrap}>
                <FiFilter className={styles.icon} />
                <select
                  value={eventGroup}
                  onChange={e => {
                    setEventGroup(e.target.value);
                    if (e.target.value) setEventType('');
                  }}
                  className={styles.select}
                  disabled={loading}
                  aria-label={safeT(t, 'audit.event_group', 'Evento crítico')}
                >
                  {EVENT_GROUP_OPTIONS.map(o => (
                    <option key={o.value || 'all'} value={o.value}>
                      {safeT(t, o.key, o.fallback)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_actor', 'Actor (ID)')}</span>
              <input
                value={actorUserId}
                onChange={e => setActorUserId(e.target.value)}
                className={styles.input}
                placeholder={safeT(t, 'audit.actor_user_id', 'actor_user_id')}
                disabled={loading}
              />
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_user_name', 'Nombre de usuario')}</span>
              <input
                value={userName}
                onChange={e => setUserName(e.target.value)}
                className={styles.input}
                placeholder={safeT(t, 'audit.user_name', 'nombre de usuario')}
                disabled={loading}
              />
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_subject', 'Sujeto (ID)')}</span>
              <input
                value={subjectUserId}
                onChange={e => setSubjectUserId(e.target.value)}
                className={styles.input}
                placeholder={safeT(t, 'audit.subject_user_id', 'subject_user_id')}
                disabled={loading}
              />
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_origin', 'Origen')}</span>
              <div className={styles.selectWrap}>
                <FiFilter className={styles.icon} />
                <select
                  value={origin}
                  onChange={e => setOrigin(e.target.value)}
                  className={styles.select}
                  disabled={loading}
                  aria-label={safeT(t, 'audit.origin', 'Origen')}
                >
                  {ORIGIN_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {safeT(t, o.key, o.fallback)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_result', 'Resultado')}</span>
              <div className={styles.selectWrap}>
                <FiFilter className={styles.icon} />
                <select
                  value={result}
                  onChange={e => setResult(e.target.value)}
                  className={styles.select}
                  disabled={loading}
                  aria-label={safeT(t, 'audit.result', 'Resultado')}
                >
                  {RESULT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {safeT(t, o.key, o.fallback)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_order', 'Orden')}</span>
              <div className={styles.selectWrap}>
                <FiFilter className={styles.icon} />
                <select
                  value={order}
                  onChange={e => setOrder(e.target.value as Order)}
                  className={styles.select}
                  disabled={loading}
                  aria-label={safeT(t, 'audit.order', 'Orden')}
                >
                  {ORDER_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {safeT(t, o.labelKey, o.fallback)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_from', 'Desde')}</span>
              <label className={styles.dateField}>
                <input
                  type="date"
                  value={from}
                  onChange={e => {
                    setFrom(e.target.value);
                    setDatePreset('custom');
                  }}
                  className={styles.dateInput}
                  disabled={loading}
                />
              </label>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_to', 'Hasta')}</span>
              <label className={styles.dateField}>
                <input
                  type="date"
                  value={to}
                  onChange={e => {
                    setTo(e.target.value);
                    setDatePreset('custom');
                  }}
                  className={styles.dateInput}
                  disabled={loading}
                />
              </label>
            </div>

            <div className={styles.filterItem}>
              <span className={styles.filterLabel}>{safeT(t, 'audit.filter_page_size', 'Tamaño')}</span>
              <div className={styles.selectWrap}>
                <FiFilter className={styles.icon} />
                <select
                  value={String(pageSize)}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className={styles.select}
                  disabled={loading}
                  aria-label={safeT(t, 'audit.page_size', 'Tamaño')}
                >
                  {PAGE_SIZE_OPTIONS.map(n => (
                    <option key={n} value={String(n)}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.filterActions}>
              <button
                type="button"
                className={styles.resetBtn}
                onClick={() => {
                  const now = new Date();
                  const past = new Date();
                  past.setDate(past.getDate() - 7);
                  setQ('');
                  setEventType('');
                  setEventGroup('');
                  setActorUserId('');
                  setSubjectUserId('');
                  setUserName('');
                  setOrigin('all');
                  setResult('all');
                  setOrder('desc');
                  setPageSize(20);
                  setFrom(toISODate(past));
                  setTo(toISODate(now));
                  setDatePreset('last7');
                  load({ resetPage: true });
                }}
                disabled={loading}
              >
                {safeT(t, 'audit.filter_reset', 'Limpiar')}
              </button>
            </div>
          </div>

          {activeFilters.length ? (
            <div className={styles.activeFilters}>
              {activeFilters.map(filter => (
                <span key={filter.id} className={styles.filterChip}>
                  {filter.label}
                  <button
                    type="button"
                    className={styles.filterChipBtn}
                    onClick={() => removeFilter(filter.id)}
                    aria-label={safeT(t, 'audit.filter_remove', 'Quitar filtro')}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          <div className={styles.presetRow}>
            <span className={styles.presetLabel}>{safeT(t, 'audit.date_presets', 'Rango rápido')}</span>
            {([
              { value: 'today', key: 'audit.preset_today', fallback: 'Hoy' },
              { value: 'yesterday', key: 'audit.preset_yesterday', fallback: 'Ayer' },
              { value: 'last7', key: 'audit.preset_last7', fallback: 'Últimos 7 días' },
              { value: 'last30', key: 'audit.preset_last30', fallback: 'Últimos 30 días' },
              { value: 'custom', key: 'audit.preset_custom', fallback: 'Personalizado' }
            ] as const).map(preset => (
              <button
                key={preset.value}
                type="button"
                className={`${styles.presetBtn} ${datePreset === preset.value ? styles.presetBtnActive : ''}`}
                onClick={() => setDatePreset(preset.value)}
                disabled={loading}
                aria-pressed={datePreset === preset.value}
              >
                {safeT(t, preset.key, preset.fallback)}
              </button>
            ))}
          </div>

          {error ? <div className={styles.error}>{error}</div> : null}

          <div className={styles.metaRow}>
            <div className={styles.metaText}>
              {loading ? '—' : `${safeT(t, 'audit.total', 'Total')}: ${String(meta?.total ?? 0)}`}
            </div>

            <div className={styles.pager}>
              <button type="button" className={styles.pagerBtn} onClick={goPrev} disabled={loading || !canPrev}>
                ‹ {safeT(t, 'audit.prev', 'Anterior')}
              </button>
              <div className={styles.pagerInfo}>
                {safeT(t, 'audit.page', 'Página')} {String(meta?.page ?? page)} / {String(meta?.totalPages ?? 1)}
              </div>
              <button type="button" className={styles.pagerBtn} onClick={goNext} disabled={loading || !canNext}>
                {safeT(t, 'audit.next', 'Siguiente')} ›
              </button>
            </div>
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{safeT(t, 'audit.th_time', 'Fecha')}</th>
                <th>{safeT(t, 'audit.th_type', 'Evento')}</th>
                <th>{safeT(t, 'audit.th_actor', 'Actor')}</th>
                <th>{safeT(t, 'audit.th_origin', 'Origen')}</th>
                <th>{safeT(t, 'audit.th_result', 'Resultado')}</th>
                <th>{safeT(t, 'audit.th_ip', 'IP')}</th>
                <th className={styles.thActions}>{safeT(t, 'common.actions', 'Acciones')}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className={styles.skeletonRow}>
                    <td colSpan={7}>
                      <div className={styles.skeletonLine} />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className={styles.empty}>
                      <div className={styles.emptyIcon}>
                        <FiInfo />
                      </div>
                      <div className={styles.emptyTitle}>
                        {safeT(t, 'audit.empty_title', 'Sin eventos para los filtros seleccionados.')}
                      </div>
                      <div className={styles.emptyText}>
                        {safeT(t, 'audit.empty_hint', 'Prueba cambiando rango de fechas o quitando filtros.')}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map(ev => (
                  <tr key={ev.event_id} className={styles.dataRow}>
                    <td>
                      <div className={styles.cellMain}>{formatDateTime(ev.occurred_at, locale)}</div>
                      <div className={styles.cellSub}>#{ev.event_id}</div>
                    </td>

                    <td>
                      <div className={styles.cellMain}>{formatEventLabel(ev.event_type, t)}</div>
                      {ev.error_code ? <div className={styles.cellSub}>error_code: {ev.error_code}</div> : null}
                    </td>

                    <td>
                      <div className={styles.cellMain}>{ev.actor_name || ev.actor_user_id || '—'}</div>
                      <div className={styles.cellSub}>
                        {ev.actor_role || '—'}
                        {ev.actor_user_id ? ` · #${ev.actor_user_id}` : ''}
                      </div>
                    </td>

                    <td>
                      <Chip value={ev.origin} tone="neutral" />
                    </td>

                    <td>
                      <Chip value={ev.result} tone={ev.result === 'SUCCESS' ? 'ok' : ev.result === 'FAIL' ? 'bad' : 'neutral'} />
                    </td>

                    <td>
                      <div className={styles.cellMain}>{ev.ip_address || '—'}</div>
                      <div className={styles.cellSub} title={ev.user_agent || ''}>
                        {(ev.user_agent || '').slice(0, 42)}
                        {(ev.user_agent || '').length > 42 ? '…' : ''}
                      </div>
                    </td>

                    <td className={styles.tdActions}>
                      <button
                        type="button"
                        className={styles.viewBtn}
                        onClick={() => setSelected(ev)}
                        aria-label="Ver detalle"
                      >
                        {safeT(t, 'common.view_details', 'Ver Detalles')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        open={!!selected}
        title={safeT(t, 'audit.detail_title', 'Detalle de evento')}
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <div className={styles.detailGrid}>
            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_event_id', 'event_id')}</div>
              <div className={styles.detailValue}>{selected.event_id}</div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_event_type', 'event_type')}</div>
              <div className={styles.detailValue}>
                {formatEventLabel(selected.event_type, t)}
                <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                  {selected.event_type}
                </div>
              </div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_occurred_at', 'occurred_at')}</div>
              <div className={styles.detailValue}>{formatDateTime(selected.occurred_at, locale)}</div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_origin', 'origin')}</div>
              <div className={styles.detailValue}>{selected.origin}</div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_result', 'result')}</div>
              <div className={styles.detailValue}>{selected.result}</div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_actor_user_id', 'actor_user_id')}</div>
              <div className={styles.detailValue}>
                {selected.actor_name || selected.actor_user_id || '—'}
                {selected.actor_user_id ? (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                    #{selected.actor_user_id}
                  </div>
                ) : null}
              </div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_actor_role', 'actor_role')}</div>
              <div className={styles.detailValue}>{selected.actor_role || '—'}</div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_ip', 'ip_address')}</div>
              <div className={styles.detailValue}>{selected.ip_address || '—'}</div>
            </div>

            <div className={styles.detailItemFull}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_user_agent', 'user_agent')}</div>
              <div className={styles.detailValue}>{selected.user_agent || '—'}</div>
            </div>

            <div className={styles.detailItem}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_error_code', 'error_code')}</div>
              <div className={styles.detailValue}>{selected.error_code || '—'}</div>
            </div>

            <div className={styles.detailItemFull}>
              <div className={styles.detailLabel}>{safeT(t, 'audit.detail_details', 'details_json')}</div>
              <pre className={styles.detailJson}>
                {JSON.stringify(selected.details_json || {}, null, 2)}
              </pre>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
